import {
  BasicMaturity,
  BasicSeries,
  InterestType,
  SecurityType,
  IssueType,
  IsExtraordinaryCall,
  PriceType,
  DealVersion,
  Maturity,
  Series,
  IsQIB,
  IsBankQualified
} from '../../../types/deal.types';
import { sectorValueGetter } from '../value-getters/sector';
import { taxStatusGetterForSeries } from '../value-getters/tax-status';
import {
  CalendarFilter,
  CalendarFiltersState
} from '../../../state/calendar-filter/state';
import { callFeaturePassesFilter } from '../../../helpers/issue/call-feature';
import { DealMaturities } from '../../../helpers/issue/maturity';
import {
  DealsSeries,
  FROM_TO_DATE_REGEXP
} from '../../../helpers/deals/series';
import moment from 'moment';
import { BaseSliderFilterModel } from '../../components/filters/base-slider-filter.types';
import {
  AdvancedFilterName,
  InclusiveExclusiveFilterModel
} from '../../../helpers/filters/filter-types';
import { YEARS_LIMIT } from '../value-getters/structure';
import { RatingEnhancementFilterModel } from './ratings/ratings-filters';
import { FilterResultFacade } from '../../../helpers/filters/filter-result-facade';
import { CalendarGridColumnName } from '../../types/column.types';
import { DealsMaturities } from '../../../helpers/deals/maturity';
import { EnhancementSeriesLevelFilter } from './ratings/enhancement-series-level-filter';
import { RatingSeriesLevelFilter } from './ratings/ratings-series-level-filter';
import { EnhancementMaturityLevelFilter } from './ratings/enhancement-maturity-level-filter';
import { CouponMaturityLevelFilter } from '../../../helpers/filters/maturity-level-filters/coupon-maturity-level-filter';

export interface SeriesMap {
  [key: string]: BasicSeries;
}

export function filterMaturities(
  aDealVersion: DealVersion,
  context: any,
  underlyingView: boolean
): Maturity[] {
  const filterResult: FilterResultFacade = context && context.getFilterResult();
  const advancedFilters: Partial<CalendarFiltersState> =
    (context && context.getAdvancedFilters()) || {};

  const {
    sourceOfRepayment,
    issueType,
    firstCallDate,
    coupon,
    isExtraordinaryCall,
    price,
    firstCouponDate,
    datedDate,
    state,
    taxStatus,
    type,
    dealStructure,
    ratings,
    sector,
    purpose,
    maturityYear,
    qib,
    isBankQualified,
    esgCertification
  } = advancedFilters;

  const dealId = aDealVersion.id;
  const dealResult = filterResult.getDealFilterResult(dealId);
  if (dealResult && dealResult.subfiltersPassComplete) {
    return aDealVersion.maturities.filter((mat) => {
      const entity = dealResult.maturities[mat.id];
      return entity && entity.filterResult;
    });
  }

  const matchingSeriesById = aDealVersion.series
    .filter((series: BasicSeries) => {
      return (
        filterState(filterResult, dealId, state ? state.model : null)(series) &&
        filterTaxStatus(
          filterResult,
          dealId,
          taxStatus ? taxStatus.model : null
        )(series) &&
        filterSector(
          filterResult,
          dealId,
          sector ? sector.model : null
        )(series) &&
        filterRatings(
          filterResult,
          dealId,
          ratings ? ratings.model : null,
          aDealVersion.maturities,
          underlyingView
        )(series) &&
        filterSourceOfRepayment(
          filterResult,
          dealId,
          sourceOfRepayment
        )(series) &&
        filterPurpose(filterResult, dealId, purpose)(series) &&
        filterIssueType(filterResult, dealId, issueType)(series) &&
        filterFirstCouponDate(filterResult, dealId, firstCouponDate)(series) &&
        filterDatedDate(filterResult, dealId, datedDate)(series) &&
        filterIsExtraordinaryCall(
          filterResult,
          dealId,
          isExtraordinaryCall
        )(series) &&
        filterBQ(filterResult, dealId, isBankQualified)(series) &&
        filterQIB(filterResult, dealId, qib)(series) &&
        filterESGCertification(filterResult, dealId, esgCertification)(series)
      );
    })
    .reduce(
      (acc: SeriesMap, series: Series) => ({
        ...acc,
        [series.id]: series
      }),
      {} as SeriesMap
    );
  const result = aDealVersion.maturities.filter(
    (mat: Maturity) =>
      DealMaturities.Structure.isShared(mat) &&
      !!matchingSeriesById[mat.seriesId] &&
      filterPrice(filterResult, dealId, price, mat) &&
      filterFirstCallMaturity(
        filterResult,
        dealId,
        firstCallDate,
        matchingSeriesById
      )(mat) &&
      filterCoupon(filterResult, dealId, coupon, mat) &&
      filterMaturityType(
        filterResult,
        dealId,
        matchingSeriesById,
        type ? type.model : null
      )(mat) &&
      filterMaturity(
        filterResult,
        dealId,
        dealStructure ? dealStructure.model : null
      )(mat) &&
      filterEnhancementForMaturities(
        filterResult,
        dealId,
        ratings ? ratings.model : null,
        matchingSeriesById[mat.seriesId]
      )(mat) &&
      filterMaturityYear(filterResult, dealId, maturityYear)(mat)
  );

  const isStructureShared: boolean =
    aDealVersion.maturities.length > 0 &&
    DealMaturities.Structure.isAllShared(aDealVersion);

  const totalPar: number = aDealVersion.size / 1000;
  const filteredPar = isStructureShared
    ? result.reduce(
        (acc: number, mat: BasicMaturity) =>
          acc + DealsMaturities.getSizeInMillions(mat),
        0
      )
    : aDealVersion.size / 1000;

  filterResult.completeSubfiltersPass(dealId, totalPar, filteredPar);
  return result;
}

function filterState(
  filterResult: FilterResultFacade,
  dealId: string,
  stateFilter?: { values: string[] }
) {
  return (series: BasicSeries): boolean => {
    const result = stateFilter
      ? stateFilter.values.indexOf(series.state) > -1
      : true;
    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      CalendarGridColumnName.State,
      result
    );
    return result;
  };
}

function filterTaxStatus(
  filterResult: FilterResultFacade,
  dealId: string,
  taxStatusFilter: InclusiveExclusiveFilterModel
) {
  return (series: BasicSeries): boolean => {
    const result = taxStatusFilter
      ? taxStatusFilter.values.indexOf(taxStatusGetterForSeries(series)) > -1
      : true;
    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      CalendarGridColumnName.TaxStatus,
      result
    );
    return result;
  };
}

export function filterSector(
  filterResult: FilterResultFacade,
  dealId: string,
  sectorFilter?: { values: string[] }
) {
  return (series: BasicSeries): boolean => {
    let result = true;
    if (sectorFilter) {
      const sectors: string[] = sectorValueGetter({
        data: { series: [series] }
      });

      result = !!sectors.filter(
        (sector) => sectorFilter.values.indexOf(sector) > -1
      ).length;
    }
    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      CalendarGridColumnName.Sector,
      result
    );
    return result;
  };
}

function filterRatings(
  filterResult: FilterResultFacade,
  dealId: string,
  ratingFilter: RatingEnhancementFilterModel,
  maturities: BasicMaturity[],
  underlyingView: boolean
) {
  const enhancementFilter = new EnhancementSeriesLevelFilter(
    ratingFilter,
    maturities
  );
  const ratingsFilter = new RatingSeriesLevelFilter(
    ratingFilter,
    underlyingView
  );

  return (series: BasicSeries): boolean => {
    const enhancementResult = enhancementFilter.applyFilter(series);
    const ratingsresult = ratingsFilter.applyFilter(series);

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.seriesEnhancement,
      enhancementResult
    );

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      CalendarGridColumnName.Ratings,
      ratingsresult
    );
    return enhancementResult && ratingsresult;
  };
}

function filterMaturityType(
  filterResult: FilterResultFacade,
  dealId: string,
  filteredSeries: { [id: string]: BasicSeries },
  typeFilter?: { values: string[] }
) {
  return (maturity: BasicMaturity) => {
    let result = true;
    if (typeFilter) {
      // const matchingTypes =
      return (
        typeFilter.values.filter((type) => {
          switch (type) {
            case 'FRN':
              result =
                filteredSeries[maturity.seriesId].interestType ===
                InterestType.floatingRate;
              break;
            case 'VR':
              result =
                filteredSeries[maturity.seriesId].interestType ===
                InterestType.variableRate;
              break;
            case SecurityType.bond:
              result =
                maturity.securityType === SecurityType.bond ||
                maturity.securityType === SecurityType.zero;
              break;
            case SecurityType.note:
              result = maturity.securityType === SecurityType.note;
              break;
            case SecurityType.put:
              result =
                maturity.securityType === SecurityType.put ||
                maturity.securityType === SecurityType.option;
              break;
            default:
              result = false;
          }
          return result;
        }).length > 0
      );
    }

    filterResult.applyMaturitiesFilterResult(
      dealId,
      maturity.id,
      CalendarGridColumnName.Type,
      result
    );
    return result;
  };
}

export function filterMaturity(
  filterResult: FilterResultFacade,
  dealId: string,
  maturityFilter: BaseSliderFilterModel
) {
  const MAX_YEAR = moment().year() + YEARS_LIMIT;
  return (maturity: BasicMaturity) => {
    let result = true;
    if (maturityFilter && maturityFilter.isFilterActive) {
      result =
        maturity.chartDate.year() >= maturityFilter.minValue &&
        (maturity.chartDate.year() <= maturityFilter.maxValue ||
          maturityFilter.maxValue === MAX_YEAR);
    }

    filterResult.applyMaturitiesFilterResult(
      dealId,
      maturity.id,
      CalendarGridColumnName.Structure,
      result
    );
    return result;
  };
}

export function filterSourceOfRepayment(
  filterResult: FilterResultFacade,
  dealId: string,
  sourceOfRepayment: CalendarFilter
) {
  return (series: BasicSeries): boolean => {
    const result =
      sourceOfRepayment &&
      sourceOfRepayment.model &&
      sourceOfRepayment.model.values
        ? sourceOfRepayment.model.values.some(
            (s) => s === series.sourceOfRepayment
          )
        : true;

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.sourceOfRepayment,
      result
    );
    return result;
  };
}

function filterFirstCallMaturity(
  filterResult: FilterResultFacade,
  dealId: string,
  firstCallFilter: CalendarFilter,
  matchingSeriesById: { [id: string]: BasicSeries }
) {
  return (maturity: BasicMaturity): boolean => {
    let result = true;
    if (firstCallFilter) {
      const series = matchingSeriesById[maturity.seriesId];

      const callFeature =
        maturity.callFeature ||
        (series.callFeatures || []).find(
          (cf) => cf.securityType === maturity.securityType
        );

      result = callFeaturePassesFilter(firstCallFilter, callFeature);
    }

    filterResult.applyMaturitiesFilterResult(
      dealId,
      maturity.id,
      AdvancedFilterName.firstCall,
      result
    );
    return result;
  };
}

function filterCoupon(
  filterResult: FilterResultFacade,
  dealId: string,
  couponFilter: CalendarFilter,
  maturity: BasicMaturity
): boolean {
  let result = true;
  if (couponFilter) {
    const filter = new CouponMaturityLevelFilter(couponFilter.model);
    result = filter.applyFilter(maturity);
  }

  filterResult.applyMaturitiesFilterResult(
    dealId,
    maturity.id,
    AdvancedFilterName.coupon,
    result
  );
  return result;
}

export function filterPrice(
  filterResult: FilterResultFacade,
  dealId: string,
  priceFilter: CalendarFilter,
  maturity: BasicMaturity
): boolean {
  let result = true;
  if (priceFilter) {
    result =
      priceFilter.model.values &&
      priceFilter.model.values.reduce(
        (acc, filter: PriceType) =>
          acc || DealMaturities.priceFilterPass(filter, maturity),
        false
      );
  }

  filterResult.applyMaturitiesFilterResult(
    dealId,
    maturity.id,
    AdvancedFilterName.price,
    result
  );
  return result;
}

export function filterFirstCouponDate(
  filterResult: FilterResultFacade,
  dealId: string,
  firstCouponDate: CalendarFilter
) {
  return (series: BasicSeries): boolean => {
    let result = true;
    if (firstCouponDate) {
      const model = firstCouponDate.model;
      const from = moment.utc(model.from, 'MM/YYYY').startOf('month');
      const to = moment.utc(model.to, 'MM/YYYY').endOf('month');
      const isDateValid = (date) => FROM_TO_DATE_REGEXP.test(date);

      result =
        !!series.firstCouponDate &&
        isDateValid(model.from) &&
        isDateValid(model.to) &&
        series.firstCouponDate.isBetween(from, to);
    }

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.firstCouponDate,
      result
    );
    return result;
  };
}

export function filterDatedDate(
  filterResult: FilterResultFacade,
  dealId: string,
  datedDate: CalendarFilter
) {
  return (series: BasicSeries): boolean => {
    let result = true;
    if (datedDate) {
      const model = datedDate.model;
      const from = moment.utc(model.from, 'MM/YYYY').startOf('month');
      const to = moment.utc(model.to, 'MM/YYYY').endOf('month');
      const isDateValid = (date) => FROM_TO_DATE_REGEXP.test(date);

      result =
        !!series.datedDate &&
        isDateValid(model.from) &&
        isDateValid(model.to) &&
        series.datedDate.isBetween(from, to);
    }
    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      CalendarGridColumnName.DatedDate,
      result
    );
    return result;
  };
}

export function filterIssueType(
  filterResult: FilterResultFacade,
  dealId: string,
  issueType: CalendarFilter
) {
  const issueTypeKeys = Object.keys(IssueType);
  const propertyNames =
    issueType && issueType.model && issueType.model.values
      ? issueType.model.values.map((filter) =>
          issueTypeKeys.find(
            (prop) => IssueType[prop] === (issueType ? filter : issueType)
          )
        )
      : [];

  return (series: BasicSeries): boolean => {
    let result = true;
    if (issueType) {
      const typeEnabled = propertyNames.some(
        (propertyName) => !!series[propertyName] && series[propertyName].enabled
      );
      const noneEnabled =
        !typeEnabled &&
        Object.keys(IssueType).every(
          (prop) => !series[prop] || !series[prop].enabled
        );

      // Either we have a match, or nothing is enabled and the default is New Money
      result =
        typeEnabled ||
        (!!issueType.model.values.find(
          (filter) => filter === IssueType.newMoney
        ) &&
          noneEnabled);
    }

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      CalendarGridColumnName.IssueType,
      result
    );
    return result;
  };
}

export function filterPurpose(
  filterResult: FilterResultFacade,
  dealId: string,
  purpose: CalendarFilter
) {
  return (series: BasicSeries): boolean => {
    const filterValues = purpose && purpose.model.values;
    let result = true;
    if (!!purpose && !!filterValues) {
      const subsectors = series.subSectors
        .map((s) =>
          !DealsSeries.Sector.getSubSectors()[s].ignorable
            ? DealsSeries.Sector.getSubSectors()[s].label
            : null
        )
        .filter((s) => s !== null);
      result =
        filterValues.length &&
        filterValues.some((value) => subsectors.some((s) => s === value));
    }

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.purpose,
      result
    );
    return result;
  };
}

export function filterIsExtraordinaryCall(
  filterResult: FilterResultFacade,
  dealId: string,
  isExtraordinaryCall: CalendarFilter
) {
  return (series: BasicSeries): boolean => {
    const result = isExtraordinaryCall
      ? !!IsExtraordinaryCall[isExtraordinaryCall.model.value] ===
        series.isExtraordinaryCall
      : true;

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.extraordinaryCall,
      result
    );
    return result;
  };
}

export function filterMaturityYear(
  filterResult: FilterResultFacade,
  dealId: string,
  maturityYear: CalendarFilter
) {
  return (maturity: BasicMaturity) => {
    const result = maturityYear
      ? filterMaturityByYear(maturityYear.model, maturity)
      : true;

    filterResult.applyMaturitiesFilterResult(
      dealId,
      maturity.id,
      AdvancedFilterName.maturityYear,
      result
    );
    return result;
  };
}

export function filterMaturityByYear(
  yearFilter: {
    year: number;
    future: boolean;
  },
  maturity: BasicMaturity
): boolean {
  return yearFilter.future
    ? maturity.chartDate.year() >= yearFilter.year
    : maturity.chartDate.year() === yearFilter.year;
}

function filterEnhancementForMaturities(
  filterResult: FilterResultFacade,
  dealId: string,
  ratingFilter: RatingEnhancementFilterModel,
  matchingSeriesById: BasicSeries
) {
  const maturityLevelEnhancementFilter = new EnhancementMaturityLevelFilter(
    ratingFilter,
    matchingSeriesById
  );
  return (maturity: BasicMaturity) => {
    const result = maturityLevelEnhancementFilter.applyFilter(maturity);

    filterResult.applyMaturitiesFilterResult(
      dealId,
      maturity.id,
      AdvancedFilterName.maturityEnhancement,
      result
    );
    return result;
  };
}

export function filterQIB(
  filterResult: FilterResultFacade,
  dealId: string,
  isQIB
) {
  return (series: BasicSeries): boolean => {
    const result = isQIB ? !!IsQIB[isQIB.model.value] === series.isQIB : true;

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.qib,
      result
    );
    return result;
  };
}

export function filterBQ(
  filterResult: FilterResultFacade,
  dealId: string,
  isBankQualified
) {
  return (series: BasicSeries): boolean => {
    const result =
      isBankQualified && series.isBankQualified !== null
        ? !!IsBankQualified[isBankQualified.model.value] ===
          series.isBankQualified
        : true;

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.bankQualified,
      result
    );
    return result;
  };
}

export function filterESGCertification(
  filterResult: FilterResultFacade,
  dealId: string,
  isESGCertification
) {
  return (series: BasicSeries): boolean => {
    const result =
      series && series.esgCertification && isESGCertification
        ? isESGCertification.model.selectedTypes.values.some(
            (ea) => ea === series.esgCertification.esgCertType
          ) &&
          isESGCertification.model.selectedVerifiers.values.some(
            (ea) => ea === series.esgCertification.esgCertVerifier
          ) &&
          (isESGCertification.model.selectedPreQualified === undefined ||
            !!series.esgCertification.esgPreapprovedInd ===
              isESGCertification.model.selectedPreQualified)
        : true;

    filterResult.applySeriesFilterResult(
      dealId,
      series.id,
      AdvancedFilterName.esgCertification,
      result
    );

    return result;
  };
}
